import { ProjectStatusBadge } from '../ProjectStatusBadge';
import { NEIGHBORHOOD_WINDOW_DAYS } from '../../api/projects.api';
import { groupNeighborhoodByDay, isNeighborhoodEmpty } from '../../lib/neighborhood';
import { messages } from '@/shared/constants/messages';
import { formatDayMonth } from '@/shared/lib/dates';
import { cn } from '@/shared/lib/utils';
import type { DateNeighborhoodItem, Id } from '@/shared/types/domain';

interface DateNeighborhoodProps {
  currentProjectId: Id;
  /** Do projeto em análise — é o que garante o nó do dia dele na faixa. */
  currentStartsAt: string | null;
  /** O outro extremo da janela: ela é ± N dias em torno do EVENTO, não de um ponto. */
  currentEndsAt: string | null;
  items: readonly DateNeighborhoodItem[];
  isLoading: boolean;
  /** 375px (§6.4): a faixa entra recolhida por padrão. */
  collapsedByDefault?: boolean;
}

/**
 * O intervalo que a consulta realmente cobre, derivado da MESMA constante que a
 * monta (decisão 38). Escrever "14 dias antes e depois" obrigaria o leitor a
 * fazer a conta; escrever as datas à mão sairia de sincronia no dia em que a
 * janela mudar.
 */
function windowLabel(startsAt: string | null, endsAt: string | null): string | null {
  if (!startsAt || !endsAt) return null;

  const offset = NEIGHBORHOOD_WINDOW_DAYS * 86_400_000;
  const from = new Date(new Date(startsAt).getTime() - offset);
  const to = new Date(new Date(endsAt).getTime() + offset);
  return messages.review.neighborhoodRange(formatDayMonth(from), formatDayMonth(to));
}

/**
 * Vizinhança da data (§6.5, decisão 20) — **um nó por DIA**, com o dia da
 * semana. Contexto, não lista: cada linha é texto, nunca link.
 *
 * ⚠️ **Tem de se distinguir da Linha do tempo**, que é o outro fio vertical da
 * mesma tela (`ProjectTimeline`). No desktop as duas estão em colunas
 * diferentes; em 375px caem no mesmo fluxo, e é ali que a distinção precisa
 * aguentar. A convenção: **histórico = fio sólido e bolinha cheia; vizinhança =
 * fio tracejado e nó vazado, com a data ao lado**. Uma é o que já aconteceu com
 * este projeto; a outra é o que mais existe perto da data dele.
 *
 * **Vazio é diferente de ausente**: janela sem mais nada mostra o dia do
 * próprio projeto, marcado, com a nota abaixo. A faixa nunca some — senão o
 * revisor não distingue "período livre" de "não carregou".
 *
 * ⚠️ **O título diz a janela que ela realmente cobre** (decisão 38). Era "Nesta
 * semana", e a consulta cobre 14 dias para cada lado: quem lia acreditava no
 * título e concluía que a igreja tinha menos coisa marcada do que tem.
 */
export function DateNeighborhood({
  currentProjectId,
  currentStartsAt,
  currentEndsAt,
  items,
  isLoading,
  collapsedByDefault = false,
}: DateNeighborhoodProps) {
  const days = groupNeighborhoodByDay(items, { id: currentProjectId, startsAt: currentStartsAt });
  const nothingElse = isNeighborhoodEmpty(days, currentProjectId);
  const range = windowLabel(currentStartsAt, currentEndsAt);

  const body = isLoading ? (
    <p className="text-sm text-muted-foreground">Carregando…</p>
  ) : days.length === 0 ? (
    <p className="text-sm text-muted-foreground">{messages.review.neighborhoodEmpty}</p>
  ) : (
    <>
      {/* `border-dashed` é metade da distinção com a linha do tempo; o nó
          vazado abaixo é a outra metade. */}
      <ol className="space-y-3 border-l border-dashed border-border pl-4">
        {days.map((day) => (
          <li key={day.key} className="relative">
            <span
              aria-hidden="true"
              className={cn(
                // Nó VAZADO (fundo do cartão + anel), contra a bolinha cheia da
                // linha do tempo. `bg-card` é o que o faz "furar" o fio.
                'absolute -left-[1.3125rem] top-1 size-2.5 rounded-full border bg-card',
                day.isCurrentDay ? 'border-primary' : 'border-border',
              )}
            />
            <p
              className={cn(
                'text-xs font-medium',
                day.isCurrentDay ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              {/* Dia da semana primeiro: quem planeja evento de igreja pensa em
                  sábado e domingo antes de pensar em número. */}
              {day.weekday} {day.label}
            </p>

            <ul className="mt-1 space-y-1">
              {day.items.map((item) => {
                const isCurrent = item.id === currentProjectId;

                return (
                  <li
                    key={item.id}
                    className={cn(
                      'flex items-center justify-between gap-2 text-sm',
                      isCurrent ? 'font-medium text-primary' : 'text-foreground',
                    )}
                  >
                    <span className="min-w-0 truncate">{item.title ?? 'Sem título'}</span>
                    {isCurrent ? (
                      <span className="shrink-0 text-xs">
                        {messages.review.neighborhoodCurrentMarker}
                      </span>
                    ) : (
                      <ProjectStatusBadge project={item} className="shrink-0" />
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>

      {nothingElse ? (
        <p className="mt-2 text-sm text-muted-foreground">{messages.review.neighborhoodEmpty}</p>
      ) : null}
    </>
  );

  // `<details>`: recolher no celular sem estado próprio nem dependência nova.
  if (collapsedByDefault) {
    return (
      <details className="group space-y-2">
        {/* Título e intervalo na MESMA linha se couber; senão o intervalo quebra
            para baixo — `flex-wrap` com `gap-x`, e não duas linhas fixas. */}
        <summary className="cursor-pointer list-none">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground group-open:text-foreground">
              {messages.review.neighborhoodTitle}
            </span>
            {range ? <span className="text-xs text-muted-foreground">{range}</span> : null}
          </span>
        </summary>
        <div className="pt-2">{body}</div>
      </details>
    );
  }

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {messages.review.neighborhoodTitle}
        </h3>
        {range ? <p className="text-xs text-muted-foreground">{range}</p> : null}
      </div>
      {body}
    </div>
  );
}
